import { useEffect, useState } from 'react';
import { useKidsStore, KidProfile } from '../../store/kidsStore';
import { useTenant } from '../../contexts/TenantContext';
import { toast } from 'sonner';
import KidProfileCard from '../../components/kids/KidProfileCard';
import KidProfileModal from '../../components/kids/KidProfileModal';

export default function KidProfilesPage() {
  const tenant = useTenant();
  const { profiles, fetchProfiles, deleteProfile } = useKidsStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<KidProfile | null>(null);
  const [stats, setStats] = useState({
    totalKids: 0,
    totalPartidas: 0,
    totalMinutos: 0
  });

  useEffect(() => {
    loadProfiles();
  }, []);

  const loadProfiles = async () => {
    await fetchProfiles();
  };

  useEffect(() => {
    // Calcular estadísticas generales
    if (profiles.length > 0) {
      const { getStats } = useKidsStore.getState();

      Promise.all(profiles.map(p => getStats(p.id)))
        .then(allStats => {
          const totals = allStats.reduce((acc, stat) => ({
            totalPartidas: acc.totalPartidas + stat.total_partidas,
            totalMinutos: acc.totalMinutos + stat.tiempo_total_minutos
          }), { totalPartidas: 0, totalMinutos: 0 });

          setStats({
            totalKids: profiles.length,
            totalPartidas: totals.totalPartidas,
            totalMinutos: totals.totalMinutos
          });
        });
    }
  }, [profiles]);

  const handleEdit = (profile: KidProfile) => {
    setSelectedProfile(profile);
    setIsModalOpen(true);
  };

  const handleDelete = async (profile: KidProfile) => {
    if (!confirm(`¿Estás seguro de que deseas eliminar el perfil de ${profile.nombre}?\n\nEsta acción no se puede deshacer.`)) {
      return;
    }

    try {
      await deleteProfile(profile.id);
      toast.success('Perfil eliminado exitosamente');
    } catch (error: any) {
      toast.error(error.message || 'Error al eliminar perfil');
    }
  };

  const handleCreateNew = () => {
    setSelectedProfile(null);
    setIsModalOpen(true);
  };

  return (
    <div
      className="min-h-screen py-12 px-4"
      style={{
        background: tenant.theme.colors.gradients.main,
        fontFamily: tenant.theme.fonts.body.family
      }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h1
            className="text-4xl font-bold text-white"
            style={{ fontFamily: tenant.theme.fonts.primary.family }}
          >
            👨‍👩‍👧‍👦 Perfiles Kids
          </h1>
          <button
            onClick={handleCreateNew}
            className="px-6 py-3 rounded-xl font-bold text-lg shadow-lg transition-all hover:shadow-xl hover:-translate-y-1"
            style={{
              background: 'white',
              color: tenant.theme.colors.primary
            }}
          >
            ➕ Crear Perfil Kid
          </button>
        </div>

        {/* Estadísticas generales */}
        {profiles.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-2xl p-6 text-center shadow-lg">
              <div className="text-5xl mb-3">👶</div>
              <div
                className="text-4xl font-bold mb-2"
                style={{ color: tenant.theme.colors.primary }}
              >
                {stats.totalKids}
              </div>
              <div className="text-gray-600 font-semibold">Perfiles Kids</div>
            </div>
            <div className="bg-white rounded-2xl p-6 text-center shadow-lg">
              <div className="text-5xl mb-3">🎮</div>
              <div
                className="text-4xl font-bold mb-2"
                style={{ color: tenant.theme.colors.primary }}
              >
                {stats.totalPartidas}
              </div>
              <div className="text-gray-600 font-semibold">Partidas Totales</div>
            </div>
            <div className="bg-white rounded-2xl p-6 text-center shadow-lg">
              <div className="text-5xl mb-3">⏱️</div>
              <div
                className="text-4xl font-bold mb-2"
                style={{ color: tenant.theme.colors.primary }}
              >
                {stats.totalMinutos}
              </div>
              <div className="text-gray-600 font-semibold">Minutos Jugados</div>
            </div>
          </div>
        )}

        {/* Lista de perfiles */}
        {profiles.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {profiles.map((profile) => (
              <KidProfileCard
                key={profile.id}
                profile={profile}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
        ) : (
          /* Empty state */
          <div className="bg-white rounded-3xl p-16 text-center shadow-lg">
            <div className="text-8xl mb-6">👨‍👩‍👧‍👦</div>
            <h2
              className="text-3xl font-bold mb-4"
              style={{ color: tenant.theme.colors.primary }}
            >
              ¡Bienvenido a Neureka Kids!
            </h2>
            <p className="text-gray-600 text-lg mb-8 leading-relaxed">
              Crea perfiles para tus hijos y permite que entrenen su mente de forma segura y divertida.
              <br /><br />
              Podrás controlar el tiempo de juego, seguir su progreso y adaptar los juegos a su edad.
            </p>
            <button
              onClick={handleCreateNew}
              className="px-8 py-4 rounded-xl font-bold text-lg shadow-lg transition-all hover:shadow-xl hover:-translate-y-1"
              style={{
                background: tenant.theme.colors.primary,
                color: 'white'
              }}
            >
              ➕ Crear Primer Perfil Kid
            </button>
          </div>
        )}
      </div>

      {/* Modal */}
      <KidProfileModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        profile={selectedProfile}
      />
    </div>
  );
}
